/**
 * useLeadSources - Hook central para o catalogo hibrido de Origens de Lead
 *
 * Retorna as origens de SISTEMA (imutaveis, hardcoded no seed) + as origens
 * CUSTOM cadastradas pelo tenant via `crm_lead_sources`. Chama a RPC
 * `get_tenant_lead_sources` (SECURITY DEFINER, sem SELECT direto do frontend).
 *
 * Efeito colateral: sempre que o resultado chega, popula o registry dinamico
 * em `src/lib/lead-sources.ts` via `registerDynamicLeadSources()`. Assim
 * componentes que ja usam `getLeadSourceLabel(id)` / `getLeadSourceIcon(id)`
 * passam a resolver origens custom automaticamente, sem refactor.
 *
 * RELEASE: 2026-07-10 v1.0.0
 */
import { useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  registerDynamicLeadSources,
  type LeadSourceEntry,
} from '@/lib/lead-sources';
import { createLogger } from '@/lib/logger';
import { toast } from 'sonner';

const logger = createLogger('LeadSources');

/**
 * Shape bruto retornado pela RPC (nomes das colunas em snake_case)
 */
interface RpcRow {
  id: string;
  label: string;
  icon: string | null;
  color: string | null;
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
}

/**
 * Converte linha da RPC para o formato consumido pelo frontend.
 */
function toEntry(r: RpcRow): LeadSourceEntry {
  return {
    id: r.id,
    label: r.label,
    iconName: r.icon,
    color: r.color,
    is_system: r.is_system,
    is_active: r.is_active,
    sort_order: r.sort_order,
  };
}

/**
 * Hook principal: lista origens (sistema + custom do tenant)
 *
 * @param includeInactive - Se true, inclui origens desativadas (uso na tela de gestao).
 */
export function useLeadSources(includeInactive = false) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const query = useQuery({
    queryKey: ['crm-lead-sources', tenantId, includeInactive],
    queryFn: async () => {
      logger.debug('Buscando origens de lead', { tenantId, includeInactive });
      const { data, error } = await (supabase.rpc as any)('get_tenant_lead_sources', {
        p_include_inactive: includeInactive,
        p_tenant_hint: tenantId,
      });
      if (error) {
        logger.error('Erro ao buscar origens', error);
        throw error;
      }
      return ((data ?? []) as RpcRow[]).map(toEntry);
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  // Sempre que os dados carregam, popula o registry global.
  // Isso garante que componentes usando getLeadSourceLabel(id) sem props
  // (badges, cards, filtros ja existentes) resolvam origens custom.
  useEffect(() => {
    if (query.data) {
      registerDynamicLeadSources(query.data);
    }
  }, [query.data]);

  // Filtra apenas ativas para uso em Selects (sempre)
  const activeOnly = useMemo(
    () => (query.data ?? []).filter(s => s.is_active),
    [query.data]
  );

  return {
    sources: query.data ?? [],
    /** Apenas ativas — use em Selects de criacao/edicao */
    activeSources: activeOnly,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Payload para upsert de origem custom
 */
export interface UpsertLeadSourceInput {
  /** Slug opcional; se vazio, e gerado do label */
  id?: string;
  label: string;
  icon?: string | null;
  color?: string | null;
  sort_order?: number;
  is_active?: boolean;
}

/**
 * Mutation: cria ou edita uma origem custom.
 */
export function useUpsertLeadSource() {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useMutation({
    mutationFn: async (input: UpsertLeadSourceInput) => {
      const { data, error } = await (supabase.rpc as any)('upsert_tenant_lead_source', {
        p_id: input.id ?? null,
        p_label: input.label,
        p_icon: input.icon ?? null,
        p_color: input.color ?? null,
        p_sort_order: input.sort_order ?? 0,
        p_is_active: input.is_active ?? true,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm-lead-sources'] });
      qc.invalidateQueries({ queryKey: ['deal-filter-options'] });
      toast.success('Origem salva com sucesso');
    },
    onError: (err: any) => {
      logger.error('Falha ao salvar origem', err);
      toast.error(err?.message || 'Erro ao salvar origem');
    },
  });
}

/**
 * Mutation: exclui (ou desativa se estiver em uso) uma origem custom.
 */
export function useDeleteLeadSource() {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await (supabase.rpc as any)('delete_tenant_lead_source', {
        p_id: id,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
      return data as { mode: 'hard_deleted' | 'soft_deleted'; deals_using: number };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['crm-lead-sources'] });
      qc.invalidateQueries({ queryKey: ['deal-filter-options'] });
      if (result.mode === 'soft_deleted') {
        toast.success(
          `Origem desativada — ${result.deals_using} negócio(s) ainda a utilizam.`
        );
      } else {
        toast.success('Origem excluída com sucesso');
      }
    },
    onError: (err: any) => {
      logger.error('Falha ao excluir origem', err);
      toast.error(err?.message || 'Erro ao excluir origem');
    },
  });
}
